import { useState, useEffect, useCallback } from "react";
import { API } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, Wifi, WifiOff, Loader2 } from "lucide-react";

// ── Platform Health Widget ──────────────────────────────────────────
// Pings the public REX surfaces every 5 minutes and shows uptime.
// Moved off the commercial Dashboard (May 2026) — it's a sys-admin
// concern, not a sales KPI, so it now lives under Admin Console →
// System Health.
export default function PlatformHealthWidget() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/platform-health`, { credentials: "include" });
      if (res.ok) setSites(await res.json());
    } catch { /* silently fail */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  useEffect(() => {
    const interval = setInterval(fetchHealth, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  if (loading) {
    return (
      <Card data-testid="platform-health-widget" className="border border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Platform Health</h3>
          </div>
          <div className="flex items-center justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <Card data-testid="platform-health-widget" className="border border-slate-200">
        <CardContent className="py-4">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Platform Health</h3>
          </div>
          <p className="text-xs text-slate-400 text-center py-2">No health data yet. Checks run every 5 minutes.</p>
        </CardContent>
      </Card>
    );
  }

  const allUp = sites.every((s) => s.status === "up");

  const formatTime = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    } catch { return "—"; }
  };

  return (
    <Card data-testid="platform-health-widget" className="border border-slate-200">
      <CardContent className="py-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Platform Health</h3>
          </div>
          <div className="flex items-center gap-1.5">
            {allUp ? (
              <span className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                All Systems Operational
              </span>
            ) : (
              <span className="flex items-center gap-1 text-[10px] font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Issue Detected
              </span>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {sites.map((site) => {
            const isUp = site.status === "up";
            return (
              <div
                key={site.site_id}
                data-testid={`health-site-${site.site_id}`}
                className={`flex items-center justify-between rounded-lg p-2.5 border transition-all ${
                  isUp ? "bg-white border-slate-200" : "bg-red-50 border-red-200"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    isUp ? "bg-emerald-100" : "bg-red-100"
                  }`}>
                    {isUp ? (
                      <Wifi className="w-3.5 h-3.5 text-emerald-600" />
                    ) : (
                      <WifiOff className="w-3.5 h-3.5 text-red-600" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-700 truncate">{site.name}</p>
                    <p className="text-[10px] text-slate-400">
                      {isUp ? `${site.response_time_ms || "—"}ms` : (site.error || "Down")}
                      {site.last_checked && ` · ${formatTime(site.last_checked)}`}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {site.uptime_24h_pct !== null && site.uptime_24h_pct !== undefined && (
                    <span
                      data-testid={`uptime-${site.site_id}`}
                      className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        site.uptime_24h_pct >= 99 ? "bg-emerald-100 text-emerald-700"
                        : site.uptime_24h_pct >= 95 ? "bg-amber-100 text-amber-700"
                        : "bg-red-100 text-red-700"
                      }`}
                    >
                      {site.uptime_24h_pct}%
                    </span>
                  )}
                  <span className={`w-2 h-2 rounded-full ${isUp ? "bg-emerald-500" : "bg-red-500"}`} />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
